/**
 * src/push/registerNativePush.ts — NATIVE (iOS/Android) Expo push registration (web-companion spec
 * W4 / §2.5).
 *
 * Native counterpart to registerWebPush.ts. Mints an Expo push token (ExponentPushToken[…]) and
 * registers it with the backend so assignment/comms notifications reach the device. The backend
 * fans those out via the Expo push service (src/push/expoPush.ts). No-op on web and on simulators
 * (which can't obtain a token).
 *
 * Flow (enableNativePush):
 *   1. Physical device check (Device.isDevice) — emulators/simulators can't receive push.
 *   2. Notifications.requestPermissionsAsync() → must be 'granted'.
 *   3. Android: ensure a default notification channel (required for delivery).
 *   4. getExpoPushTokenAsync({ projectId }) → the Expo push token.
 *   5. POST the token to /api/push/subscribe (platform = ios | android, endpoint = the token).
 *
 * The token is stashed in expo-secure-store so logout can revoke exactly this device's subscription.
 * NEVER imports @smartbox/* (mobile invariant 1). No secrets logged.
 */
import { Platform } from 'react-native';
import * as Notifications from 'expo-notifications';
import * as Device from 'expo-device';
import Constants from 'expo-constants';
import * as SecureStore from 'expo-secure-store';
import { post, del } from '../api/client';
import type { PushSubscribeRequest } from '@beam/api-types';

const IS_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';
/** Secure-store key holding the last-registered Expo push token (so logout can revoke it). */
const NATIVE_PUSH_TOKEN_KEY = 'beam_expo_push_token';

export function isNativePushSupported(): boolean {
  return IS_NATIVE;
}

/** The EAS project id needed to mint an Expo push token (written by `eas init` into app config). */
function easProjectId(): string | undefined {
  const fromExpoConfig = Constants.expoConfig?.extra?.eas?.projectId as string | undefined;
  const fromEasConfig = (Constants as { easConfig?: { projectId?: string } }).easConfig?.projectId;
  return fromExpoConfig ?? fromEasConfig;
}

/** Show notifications received while the app is foregrounded (set once at module load). */
if (IS_NATIVE) {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      // SDK 54 split the deprecated shouldShowAlert into banner + list.
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: true,
      shouldSetBadge: false,
    }),
  });
}

/**
 * Register this device for Expo push and tell the backend. Returns a status the caller can surface.
 * Safe to call unconditionally (e.g. after login) — returns 'unsupported'/'unavailable'/'denied'
 * rather than throwing.
 */
export async function enableNativePush(): Promise<'enabled' | 'denied' | 'unsupported' | 'unavailable'> {
  if (!IS_NATIVE) return 'unsupported';
  if (!Device.isDevice) return 'unavailable'; // simulators/emulators never receive push

  const existing = await Notifications.getPermissionsAsync();
  let status = existing.status;
  if (status !== 'granted') {
    status = (await Notifications.requestPermissionsAsync()).status;
  }
  if (status !== 'granted') return 'denied';

  const projectId = easProjectId();
  if (!projectId) return 'unavailable'; // no EAS project configured → can't mint an Expo token

  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'Default',
      importance: Notifications.AndroidImportance.DEFAULT,
    });
  }

  let token: string;
  try {
    token = (await Notifications.getExpoPushTokenAsync({ projectId })).data;
  } catch {
    return 'unavailable';
  }

  const body: PushSubscribeRequest = {
    platform: Platform.OS === 'android' ? 'android' : 'ios',
    endpoint: token,
  };
  await post('/api/push/subscribe', body);
  await SecureStore.setItemAsync(NATIVE_PUSH_TOKEN_KEY, token).catch(() => undefined);
  return 'enabled';
}

/** Revoke this device's Expo push subscription server-side (called on logout). Best-effort. */
export async function disableNativePush(): Promise<void> {
  if (!IS_NATIVE) return;
  const token = await SecureStore.getItemAsync(NATIVE_PUSH_TOKEN_KEY).catch(() => null);
  if (!token) return;
  // Endpoint as a query param (the DELETE helper sends no body).
  await del(`/api/push/subscribe?endpoint=${encodeURIComponent(token)}`).catch(() => undefined);
  await SecureStore.deleteItemAsync(NATIVE_PUSH_TOKEN_KEY).catch(() => undefined);
}
